import EmberService, { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import {
	ChangeIndicatorClass,
	DataPerformanceSummaryLimitResponseType,
	DataPerformanceSummaryTrend,
	DataPerformanceSummaryType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type Block from 'webapp/models-custom/block';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBase } from 'webapp/models-custom/data-performance-summary/base';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBlock } from 'webapp/models-custom/data-performance-summary/block';
import DataPerformanceSummaryBlock from 'webapp/models-custom/data-performance-summary/block';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBoard } from 'webapp/models-custom/data-performance-summary/board';
import DataPerformanceSummaryBoard from 'webapp/models-custom/data-performance-summary/board';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectDynamic } from 'webapp/models-custom/data-performance-summary/dynamic';
import DataPerformanceSummaryDynamic from 'webapp/models-custom/data-performance-summary/dynamic';
import DataPerformanceSummaryGoal from 'webapp/models-custom/data-performance-summary/goal';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Source from 'webapp/models-custom/source';

// import type SocketService from 'webapp/services/socket';

export type DataPerformanceSummary =
	| DataPerformanceSummaryGoal
	| DataPerformanceSummaryBoard
	| DataPerformanceSummaryBlock
	| DataPerformanceSummaryDynamic;

// keyed by objectType, so createModel and its callers can narrow to the kind
export type DataPerformanceSummaryObject =
	| (DataPerformanceSummaryObjectBase & { objectType: DataPerformanceSummaryType.GOAL })
	| DataPerformanceSummaryObjectBoard
	| DataPerformanceSummaryObjectBlock
	| (DataPerformanceSummaryObjectDynamic & { objectType: DataPerformanceSummaryType.DYNAMIC });

export default class ModelManagerDataPerformanceSummariesService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;

	@tracked boardsTrendCache: Record<string, DataPerformanceSummaryTrend> = {}; // by board id

	@tracked limits: APIData.GenerativeAI.DataPerformance.SummaryLimit[] = [
		{
			type: DataPerformanceSummaryLimitResponseType.DAILY_ACCOUNT,
			remaining: Infinity,
		},
		{
			type: DataPerformanceSummaryLimitResponseType.MONTHLY_ACCOUNT,
			remaining: Infinity,
		},
		{
			type: DataPerformanceSummaryLimitResponseType.DAILY_USER,
			remaining: Infinity,
		},
	];

	get limitRemaining() {
		return this.limits.reduce(
			(min, limit) => (min < limit.remaining ? min : limit.remaining),
			Infinity,
		);
	}

	get limitReached() {
		return this.limitRemaining <= 0;
	}

	get dailyUserLimitRemaining() {
		return (
			this.limits.find(
				(limit) => limit.type === DataPerformanceSummaryLimitResponseType.DAILY_USER,
			)?.remaining || 0
		);
	}

	get limitReachedMessage() {
		if (this.limitReached) {
			const reachedLimit = this.limits.find((limit) => limit.remaining <= 0);
			if (reachedLimit?.type === DataPerformanceSummaryLimitResponseType.MONTHLY_ACCOUNT) {
				return "You've reached your monthly limit for AI-powered performance summaries. Your limit will reset at the beginning of the next month.";
			}
			return "You've reached your daily limit for AI-powered performance summaries. Come back tomorrow to generate more.";
		}
		return '';
	}

	get showInUI() {
		// Hide UI for viewer role
		return !this.currentUserService.isViewer;
	}

	get showInUIAndEnabled() {
		return this.showInUI && this.billingService.generativeAIEnabled;
	}

	getSummaries(reqBody: APIData.GenerativeAI.DataPerformance.SummariesRequest) {
		return this.requestService.api<APIData.GenerativeAI.DataPerformance.SummariesResponse>(
			'POST',
			'/summaries',
			reqBody,
			{
				timeout: 90 * 1000,
			},
		);
		// return this.requestService.api('GET', '/all', reqBody, { timeout: 60 * 1000 });
	}

	getTrend(numberValue: number, indicatorClass: string) {
		const isPositive = indicatorClass === ChangeIndicatorClass.POSITIVE;
		if (Math.abs(numberValue) <= 4) {
			return DataPerformanceSummaryTrend.NEUTRAL;
		} else if (isPositive) {
			return DataPerformanceSummaryTrend.POSITIVE;
		} else {
			return DataPerformanceSummaryTrend.NEGATIVE;
		}
	}

	getTrendDescription(trend: DataPerformanceSummaryTrend) {
		switch (trend) {
			case DataPerformanceSummaryTrend.POSITIVE:
				return htmlSafe('Showing significant <b>progress</b>');
			case DataPerformanceSummaryTrend.NEUTRAL:
				return htmlSafe('<b>Stable</b> with minor fluctuations');
			case DataPerformanceSummaryTrend.NEGATIVE:
				return htmlSafe('Performance <b>decline</b>, corrective action needed');
			default:
				return htmlSafe('Performance evaluation <b>unavailable</b>');
		}
	}

	getGoalTrendDescription(trend: DataPerformanceSummaryTrend) {
		switch (trend) {
			case DataPerformanceSummaryTrend.POSITIVE:
				return htmlSafe('Goal <b>likely to be hit</b>');
			case DataPerformanceSummaryTrend.NEUTRAL:
				return htmlSafe('<b>At risk</b> of achieving the goal');
			case DataPerformanceSummaryTrend.NEGATIVE:
				return htmlSafe('Goal <b>unlikely to be hit</b>');
			default:
				return htmlSafe('Performance evaluation <b>unavailable</b>');
		}
	}

	getTrackingMetricNamesFromBlock(block: Block) {
		const metricNames: string[] = [];

		block.dataSelects.forEach((ds) => {
			const name = ds.metricSetup.metric?.name;
			if (name) {
				metricNames.push(name);
			}
		});

		return metricNames;
	}

	trackEvent(
		eventType: TrackingEvent,
		summary: DataPerformanceSummary,
		data?: APIData.Tracking.Data,
	) {
		let eventData: APIData.Tracking.Data = {};
		switch (eventType) {
			case TrackingEvent.DATA_PERFORMANCE_SUMMARY_POPUP_OPENED:
			case TrackingEvent.DATA_PERFORMANCE_SUMMARY_REFRESHED:
				eventData = {
					...data,
					type: summary.trackingObjectType,
				};
				break;
			default:
				break;
		}

		this.analyticsService.event(eventType, {
			...eventData,
			metrics: summary.trackingMetricsNames.filter(
				(item, i, ar) => !!item && ar.indexOf(item) === i,
			),
			datasources: summary.sources.map((s: Source) => s.sourceType?.key || ''),
		});
	}

	createModel(model: DataPerformanceSummaryObject): DataPerformanceSummary {
		switch (model.objectType) {
			case DataPerformanceSummaryType.GOAL:
				return new DataPerformanceSummaryGoal(model);
			case DataPerformanceSummaryType.BOARD:
			case DataPerformanceSummaryType.BOARD_PREVIEW:
				return new DataPerformanceSummaryBoard(model);
			case DataPerformanceSummaryType.BLOCK:
				return new DataPerformanceSummaryBlock(model);
			case DataPerformanceSummaryType.DYNAMIC:
				return new DataPerformanceSummaryDynamic(model);
		}
	}

	private getLimits() {
		const limitsUrl = '/summaries/limits';
		// const limitsUrl = '/all';
		return this.requestService
			.api<APIData.GenerativeAI.DataPerformance.SummaryLimitsResponse>(
				'GET',
				limitsUrl,
				null,
				{ skipShowingErrorInfolayer: true },
			)
			.catch(() => {
				// skip
			});
	}

	updateLimits(newLimits: APIData.GenerativeAI.DataPerformance.SummaryLimit[]) {
		const limits = newLimits.reduce(
			(acc, item) => {
				acc[item.type] = item.remaining;
				return acc;
			},
			{} as Partial<
				Record<APIData.GenerativeAI.DataPerformance.SummaryLimit['type'], number>
			>,
		);

		this.limits.forEach((limit) => {
			const remaining = limits[limit.type];
			if (remaining !== undefined) {
				limit.remaining = remaining;
			}
		});

		this.limits = this.limits;
	}

	getAndUpdateLimits() {
		return this.getLimits().then((data) => {
			// eslint-disable quotes, comma-spacing, key-spacing, block-spacing, no-useless-escape
			// prettier-ignore
			// data = {"limits":[{"type":"daily_account","remaining":2},{"type":"monthly_account","remaining":350},{"type":"daily_user","remaining":0}]};
			if (data && data.limits) {
					this.updateLimits(data.limits);
				}
		});
	}

	get filterList(): FilterOption<DataPerformanceSummaryTrend | string>[] {
		return [
			{
				id: 'all',
				text: 'All',
				icon: 'CircleFill',
				color: '#8e929b', // --databox-neutral-400
			},
			{
				id: DataPerformanceSummaryTrend.POSITIVE,
				text: 'Positive',
				icon: 'CircleFill',
				color: '#25a767', // --databox-green-500
			},
			{
				id: DataPerformanceSummaryTrend.NEUTRAL,
				text: 'Neutral',
				icon: 'CircleFill',
				color: '#f5a623', // --databox-yellow-500
			},
			{
				id: DataPerformanceSummaryTrend.NEGATIVE,
				text: 'Negative',
				icon: 'CircleFill',
				color: '#e62828', // --databox-red-500
			},
			{
				id: DataPerformanceSummaryTrend.UNAVAILABLE,
				text: 'Unavailable',
				icon: 'CircleFill',
				color: '#4D71F1', // --databox-brand-500
			},
		];
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/data-performance-summaries': ModelManagerDataPerformanceSummariesService;
	}
}
